import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { cloneDeep } from "lodash";
import { TWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { TWebsiteBuilderSectionEntityLayout } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import { elementTraversal } from "../../actionHelpers/elementTraversal";

export interface IMoveElementActionPayload {
  id: string;
  prevIndex: number;
  nextIndex: number;
  prevZIndex: number;
  nextZIndex: number;
  prevSectionId: string;
  nextSectionId: string;
  prevContainerId: string;
  nextContainerId: string;
  prevGridCellId: string;
  nextGridCellId: string;
  prevTabId: string;
  nextTabId: string;
  prevLayout: TWebsiteBuilderSectionEntityLayout;
  nextLayout: TWebsiteBuilderSectionEntityLayout;
}

export interface IMoveElementAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.MOVE_ELEMENT;
  payload: IMoveElementActionPayload;
}

export function getMoveElementInverseAction(action: IMoveElementAction): IMoveElementAction {
  const {
    id,
    prevIndex,
    nextIndex,
    prevZIndex,
    nextZIndex,
    prevSectionId,
    nextSectionId,
    prevContainerId,
    nextContainerId,
    prevGridCellId,
    nextGridCellId,
    prevTabId,
    nextTabId,
    prevLayout,
    nextLayout,
  } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.MOVE_ELEMENT,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      prevIndex: nextIndex,
      nextIndex: prevIndex,
      prevZIndex: nextZIndex,
      nextZIndex: prevZIndex,
      prevSectionId: nextSectionId,
      nextSectionId: prevSectionId,
      prevContainerId: nextContainerId,
      nextContainerId: prevContainerId,
      prevGridCellId: nextGridCellId,
      nextGridCellId: prevGridCellId,
      prevTabId: nextTabId,
      nextTabId: prevTabId,
      prevLayout: cloneDeep(nextLayout),
      nextLayout: cloneDeep(prevLayout),
    },
  };
}


function moveElementToOtherParentTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  id: string,
  nextLayout: TWebsiteBuilderSectionEntityLayout,
  nextIndex: number,
  nextZIndex: number,
  prevParentId: string,
  nextParentId: string,
): IWebsiteBuilderHistorySnapshot {
  let updatedElement: TWebsiteBuilderElement = null;

  function deleteTraverse(node: IWebsiteBuilderGeneralTraversalNode): IWebsiteBuilderGeneralTraversalNode {    
    if (!node.children) {
      return node;
    } else {
      if (node.id === prevParentId) {
        const oldElement = node.children.find(child => child.id === id) as TWebsiteBuilderElement;

        if (oldElement != null && updatedElement == null) {
          updatedElement = cloneDeep(oldElement);
          
          updatedElement.element.layout = nextLayout;
          updatedElement.element.zIndex = nextZIndex;
        }

        return {
          ...node,
          children: node.children.filter(child => child.id !== id),
        };
      } else {
        return {
          ...node,
          children: node.children.map(deleteTraverse),
        };
      }
    }
  }

  const headerAfterDelete = deleteTraverse(header) as IWebsiteBuilderHeader;
  const rootAfterDelete = deleteTraverse(root) as IWebsiteBuilderRoot;
  const footerAfterDelete = deleteTraverse(footer) as IWebsiteBuilderFooter;

  if (updatedElement == null) {
    return {
      header,
      root,
      footer,
    };
  }

  function addTraverse(node: IWebsiteBuilderGeneralTraversalNode): IWebsiteBuilderGeneralTraversalNode {
    if (!node.children) {
      return node;
    } else {
      if (node.id === nextParentId) {
        return {
          ...node,
          children: [
            ...node.children.slice(0, nextIndex),
            updatedElement,
            ...node.children.slice(nextIndex),
          ],
        };
      } else {
        return {
          ...node,
          children: node.children.map(addTraverse),
        };
      }
    }
  }

  const headerAfterAdding = addTraverse(headerAfterDelete) as IWebsiteBuilderHeader;
  const rootAfterAdding = addTraverse(rootAfterDelete) as IWebsiteBuilderRoot;
  const footerAfterAdding = addTraverse(footerAfterDelete) as IWebsiteBuilderFooter;

  return {
    header: headerAfterAdding,
    root: rootAfterAdding,
    footer: footerAfterAdding,
  };
}

export function applyMoveElementChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IMoveElementAction,
): IWebsiteBuilderHistorySnapshot {
  const {
    id,
    prevSectionId,
    nextSectionId,
    prevContainerId,
    nextContainerId,
    prevGridCellId,
    nextGridCellId,
    prevTabId,
    nextTabId,
    nextLayout,
    nextZIndex,
    nextIndex,
  } = action.payload;

  const prevParentId = prevTabId ?? prevGridCellId ?? prevContainerId ?? prevSectionId;
  const nextParentId = nextTabId ?? nextGridCellId ?? nextContainerId ?? nextSectionId;

  if (prevParentId === nextParentId) {
    return elementTraversal(
      header,
      root,
      footer,
      id,
      { layout: cloneDeep(nextLayout) },
    );
  } else {
    return moveElementToOtherParentTraversal(
      header,
      root,
      footer,
      id,
      cloneDeep(nextLayout),
      nextIndex,
      nextZIndex,
      prevParentId,
      nextParentId,
    );
  }
}
